// Tray of unlinked assets (badly-named / unidentified data). Each can be toggled
// visible on the globe to identify it, flown to, then linked to a register item -
// individually, or many at once via bulk-select (multiple 3D objects per layer is
// the normal case, not the exception).
import type { Asset } from "../api";

export interface FlatItem {
  id: number;
  label: string; // "Category > Item"
}

export interface UnlinkedHandlers {
  onToggle: (asset: Asset, visible: boolean) => void;
  onFly: (asset: Asset) => void;
  onLink: (assetIds: number[], registerItemId: number) => void;
  onAdopt: (assetId: number) => void;
  isVisible?: (assetId: number) => boolean;
}

let bulkMode = false;
const selected = new Set<number>();

export function renderUnlinkedTray(
  container: HTMLElement,
  assets: Asset[],
  items: FlatItem[],
  handlers: UnlinkedHandlers,
): void {
  container.innerHTML = "";
  selected.clear();
  const countEl = document.getElementById("unlinked-count");
  if (countEl) countEl.textContent = assets.length ? String(assets.length) : "";
  if (assets.length === 0) {
    bulkMode = false;
    container.innerHTML = '<div class="hint">Nothing unlinked.</div>';
    return;
  }

  // Header: tip + bulk-mode toggle.
  const head = document.createElement("div");
  head.className = "tray-head";
  const tip = document.createElement("span");
  tip.className = "tree-summary";
  tip.textContent = bulkMode
    ? "Tick rows below, then pick the layer they all belong to."
    : "Tick to show on globe · ⌖ fly · Link / ＋ adopt.";
  const bulkBtn = document.createElement("button");
  bulkBtn.className = "filter-chip" + (bulkMode ? " active" : "");
  bulkBtn.textContent = bulkMode ? "✕ Cancel bulk" : "Bulk link…";
  bulkBtn.title = "Select several assets and link them all to ONE register layer";
  bulkBtn.addEventListener("click", () => {
    bulkMode = !bulkMode;
    renderUnlinkedTray(container, assets, items, handlers);
  });
  head.append(tip, bulkBtn);
  container.append(head);

  // Bulk action bar (appears once something is selected).
  const bulkBar = document.createElement("div");
  bulkBar.className = "bulk-bar";
  bulkBar.style.display = "none";
  container.append(bulkBar);

  function refreshBulkBar(): void {
    if (!bulkMode || selected.size === 0) {
      bulkBar.style.display = "none";
      bulkBar.innerHTML = "";
      return;
    }
    bulkBar.style.display = "";
    bulkBar.innerHTML = `<div class="bulk-count">Link ${selected.size} selected to:</div>`;
    const search = document.createElement("input");
    search.type = "text";
    search.placeholder = "Find register item…";
    search.className = "link-search";
    const list = document.createElement("div");
    list.className = "link-list";
    const draw = (filter: string) => {
      const f = filter.trim().toLowerCase();
      list.innerHTML = "";
      const matches = (f ? items.filter((it) => it.label.toLowerCase().includes(f)) : items).slice(0, 40);
      for (const it of matches) {
        const opt = document.createElement("div");
        opt.className = "link-option";
        opt.textContent = it.label;
        opt.addEventListener("click", () => {
          handlers.onLink([...selected], it.id);
          bulkMode = false;
        });
        list.append(opt);
      }
      if (!matches.length) list.innerHTML = '<div class="hint">No match</div>';
    };
    search.addEventListener("input", () => draw(search.value));
    draw("");
    bulkBar.append(search, list);
    search.focus();
  }

  for (const asset of assets) {
    container.append(renderRow(asset, items, handlers, refreshBulkBar));
  }
}

function renderRow(
  asset: Asset,
  items: FlatItem[],
  h: UnlinkedHandlers,
  refreshBulkBar: () => void,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "unlinked-item";

  const head = document.createElement("div");
  head.className = "unlinked-head";

  if (bulkMode) {
    // Selection mode: the row is a big click target for picking what to link.
    const sel = document.createElement("input");
    sel.type = "checkbox";
    sel.className = "asset-check";
    sel.title = "Select for bulk link";
    const toggleSel = (on: boolean) => {
      if (on) selected.add(asset.id);
      else selected.delete(asset.id);
      row.classList.toggle("selected", on);
      refreshBulkBar();
    };
    sel.addEventListener("change", () => toggleSel(sel.checked));
    head.addEventListener("click", (e) => {
      if (e.target === sel) return;
      sel.checked = !sel.checked;
      toggleSel(sel.checked);
    });

    const badge = document.createElement("span");
    badge.className = `badge ${asset.dimension === "2D" ? "chip" : "muted"}`;
    badge.textContent = asset.dimension;
    const name = document.createElement("span");
    name.className = "unlinked-name";
    name.textContent = asset.name;
    name.title = asset.name;
    const flyBtn = document.createElement("button");
    flyBtn.className = "link-btn";
    flyBtn.textContent = "⌖";
    flyBtn.title = "Fly to this data";
    flyBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      h.onFly(asset);
    });
    head.append(sel, badge, name, flyBtn);
    row.append(head);
    return row;
  }

  const cb = document.createElement("input");
  cb.type = "checkbox";
  cb.className = "asset-check";
  cb.title = "Show on globe to identify";
  cb.checked = h.isVisible?.(asset.id) ?? false;
  cb.addEventListener("change", () => h.onToggle(asset, cb.checked)); // show/hide only

  const badge = document.createElement("span");
  badge.className = `badge ${asset.dimension === "2D" ? "chip" : "muted"}`;
  badge.textContent = asset.dimension;

  const name = document.createElement("span");
  name.className = "unlinked-name clickable";
  name.textContent = asset.name;
  name.title = `${asset.name} - click to show and fly to it`;
  name.addEventListener("click", () => h.onFly(asset)); // click name = reveal + frame

  const flyBtn = document.createElement("button");
  flyBtn.className = "link-btn";
  flyBtn.textContent = "⌖";
  flyBtn.title = "Fly to this data";
  flyBtn.addEventListener("click", () => h.onFly(asset));

  const linkBtn = document.createElement("button");
  linkBtn.className = "link-btn";
  linkBtn.textContent = "Link…";

  const adoptBtn = document.createElement("button");
  adoptBtn.className = "link-btn";
  adoptBtn.textContent = "＋";
  adoptBtn.title = "Make this a register item (no register entry exists for it)";
  adoptBtn.addEventListener("click", () => h.onAdopt(asset.id));

  head.append(cb, badge, name, flyBtn, linkBtn, adoptBtn);
  row.append(head);

  // Inline single-asset picker, hidden until "Link…" is clicked.
  const picker = document.createElement("div");
  picker.className = "link-picker";
  picker.style.display = "none";
  const search = document.createElement("input");
  search.type = "text";
  search.placeholder = "Find register item…";
  search.className = "link-search";
  const list = document.createElement("div");
  list.className = "link-list";
  picker.append(search, list);
  row.append(picker);

  function drawList(filter: string) {
    const f = filter.trim().toLowerCase();
    list.innerHTML = "";
    const matches = (f ? items.filter((it) => it.label.toLowerCase().includes(f)) : items).slice(0, 40);
    for (const it of matches) {
      const opt = document.createElement("div");
      opt.className = "link-option";
      opt.textContent = it.label;
      opt.addEventListener("click", () => h.onLink([asset.id], it.id));
      list.append(opt);
    }
    if (!matches.length) list.innerHTML = '<div class="hint">No match</div>';
  }

  linkBtn.addEventListener("click", () => {
    const open = picker.style.display === "none";
    picker.style.display = open ? "block" : "none";
    if (open) {
      drawList("");
      search.focus();
    }
  });
  search.addEventListener("input", () => drawList(search.value));

  return row;
}
